import { getTemplateDataTableStyles } from './DataTable.styles'
import type {
  CallbackEvent,
  TableColumn,
  TableRow,
  TableRowCellButtonClick,
  TableRowCellButtonLink,
  TableRowCellDropdown,
  TableRowCellSlot,
  SelectedItemChangedEvent,
  SelectedColumnHeaderChangedEvent,
  SortChangedEvent,
} from './types/Table.type'
import {
  createSelectColumnHeader,
  createSelectColumnCell,
  getVisibleRowKeys,
  areAllVisibleRowsSelected,
  updateAllRowCheckboxesInDOM,
  updateSelectAllCheckboxInDOM,
} from './utils/DataTable.utils.checkbox'

import { getBootstrapIconUrl, BootstrapIcon } from '@/components/webcomponents/Global/icons'
import {
  tryLoadDependencies,
  tryLoadLightDOMStyles,
  WebAwesomeComponent,
} from '@/components/webcomponents/Global/loadDependency'
import { dispatchAnalyticEvent } from '@/components/webcomponents/Global/analyticsEvents'
import { createButtonLinkCell } from './utils/DataTable.utils.cellbuttonlink'
import { createSlotCell } from './utils/DataTable.utils.cellslot'
import { createButtonClickCell } from './utils/DataTable.utils.cellbuttonclick'
import { createDropdownCell } from './utils/DataTable.utils.celldropdown'
import { createSubtextCell } from './utils/DataTable.utils.cellsubtext'
import { createNoContentRow } from './utils/DataTable.utils.rownocontent'
import type { PaginationProps } from '@/components/webcomponents/Pagination/types/Pagination.type'
import { createSkeletonRows } from './utils/DataTable.utils.rowskeleton'
const svgSortUp = getBootstrapIconUrl(BootstrapIcon.SortUp)
const svgSortDown = getBootstrapIconUrl(BootstrapIcon.SortDown)

const dataTableTemplate = document.createElement('template')
dataTableTemplate.innerHTML = `
<div part="table-container" class="table-responsive ts-stack">
  <table part="table">
    <thead part="thead">
      <tr part="thead-row"></tr>
    </thead>
    <tbody part="tbody">
    </tbody>
  </table>
  <div part="pagination" class="pagination-wrapper"></div>
</div>
`

const sortableHeaderTemplate = document.createElement('template')
sortableHeaderTemplate.innerHTML = `
<wa-button exportparts="base:column-sort-button" variant="neutral" appearance="plain" size="small"><wa-icon exportparts="svg:column-sort-icon" src="${svgSortDown}" label="Sort"></wa-icon></wa-button>
`

class DataTable extends HTMLElement {
  private readonly componentName = 'ts-data-table'
  private sortColumn: string | null = null
  private sortDirection: 'asc' | 'desc' = 'asc'
  private sortColumnDataType: 'string' | 'number' | 'boolean' | 'date' | null = null
  private pageInfo: PaginationProps | null = null
  private dataColumns: TableColumn[] = []
  private dataRows: TableRow[] = []
  private _callback: ((e: CallbackEvent) => void) | null = null
  private noResultsAutoShow: boolean = true
  private noResultsShow: boolean = false
  private loadedDependencies: Set<WebAwesomeComponent> = new Set()
  private enableSelectColumn: boolean = false
  private enableSelectColumnHeader: boolean = false
  private selectedRowKeys: Set<string> = new Set()
  private isLoading: boolean = false
  private skeletonRowCount: number = 5

  constructor() {
    super()

    tryLoadDependencies(document, [WebAwesomeComponent.Skeleton, WebAwesomeComponent.Tooltip])

    const shadow = this.attachShadow({ mode: 'open' })
    shadow.appendChild(getTemplateDataTableStyles().content.cloneNode(true))
    shadow.appendChild(dataTableTemplate.content.cloneNode(true))

    tryLoadLightDOMStyles('DataTable.css')
  }

  /**
   * @deprecated Use event listeners. See docs for details.
   */
  set callback(callback: (e: CallbackEvent) => void) {
    document.AscendAwesome?.LogManager?.logWarning('callback is deprecated. See docs.')
    this._callback = callback
    // Re-render if data is already loaded to apply callback to existing buttons
    if (this.dataRows.length > 0 || this.dataColumns.length > 0) {
      this.reload()
    }
  }

  /**
   * Returns an array of row keys that are currently selected
   * @returns Array of selected row keys
   */
  getSelected(): string[] {
    return Array.from(this.selectedRowKeys)
  }

  /**
   * Sets which rows should be selected by their keys
   * @param rowKeys Array of row keys to select
   */
  setSelected(rowKeys: string[]): void {
    this.selectedRowKeys.clear()
    for (const key of rowKeys) {
      this.selectedRowKeys.add(key)
    }
    this.updateCheckboxStates()
  }

  connectedCallback() {
    dispatchAnalyticEvent('ascend_table_loaded', 'load', this.componentName)
  }

  static get observedAttributes() {
    return [
      'columns',
      'rows',
      'pagination',
      'background',
      'spacing',
      'no-results-show',
      'no-results-disable-show-auto',
      'enable-select-column',
      'enable-select-column-header',
      'loading',
      'skeleton-rows',
    ]
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    if (name === 'no-results-show') {
      this.noResultsShow = newValue === 'true'
      this.updateRows()
    } else if (name === 'no-results-disable-show-auto') {
      this.noResultsAutoShow = newValue !== 'true'
      this.updateRows()
    } else if (name === 'columns') {
      this.dataColumns = this.tryGetColumns()
      // Columns changed: rebuild everything (header + rows)
      this.updateHeader()
      this.updateRows()
    } else if (name === 'rows') {
      this.dataRows = this.tryGetRows()
      // Data changed: only update rows (preserve header and focus)
      this.updateRows()
    } else if (name === 'pagination') {
      this.pageInfo = this.tryGetPagination()
      // Pagination changed: only update rows (preserve header and focus)
      this.updateRows()
    } else if (name === 'background') {
      if (newValue === 'striped') {
        this.shadowRoot?.querySelector('table')?.classList.add('table-striped')
      } else {
        this.shadowRoot?.querySelector('table')?.classList.remove('table-striped')
      }
    } else if (name === 'spacing') {
      if (newValue === 'large') {
        this.shadowRoot?.querySelector('table')?.classList.add('table-spacing-large')
        this.shadowRoot?.querySelector('table')?.classList.remove('table-spacing-condensed')
      } else if (newValue === 'condensed') {
        this.shadowRoot?.querySelector('table')?.classList.remove('table-spacing-large')
        this.shadowRoot?.querySelector('table')?.classList.add('table-spacing-condensed')
      } else {
        this.shadowRoot?.querySelector('table')?.classList.remove('table-spacing-large')
        this.shadowRoot?.querySelector('table')?.classList.remove('table-spacing-condensed')
      }
      this.updateRows()
    } else if (name === 'enable-select-column') {
      this.enableSelectColumn = newValue === 'true'
      // Selection feature changed: rebuild everything (header + rows)
      this.updateHeader()
      this.updateRows()
    } else if (name === 'enable-select-column-header') {
      this.enableSelectColumnHeader = newValue === 'true'
      // Selection header changed: rebuild everything (header + rows)
      this.updateHeader()
      this.updateRows()
    } else if (name === 'loading') {
      this.isLoading = this.hasAttribute('loading')
      this.reload()
    } else if (name === 'skeleton-rows') {
      this.skeletonRowCount = Number(newValue) || 5
      this.reload()
    }
  }

  private tryGetColumns(): TableColumn[] {
    const columns = this.getAttribute('columns')
    if (!columns) {
      return []
    }
    try {
      return JSON.parse(columns)
    } catch (e) {
      console.error('Invalid JSON:', e)
    }
    return []
  }

  private tryGetRows(): TableRow[] {
    const rows = this.getAttribute('rows')
    if (!rows) {
      return []
    }
    try {
      return JSON.parse(rows)
    } catch (e) {
      console.error('Invalid JSON:', e)
    }
    return []
  }

  private tryGetPagination(): PaginationProps | null {
    const dataPagination = this.getAttribute('pagination')
    if (!dataPagination) {
      return null
    }
    try {
      const parsedPagination = JSON.parse(dataPagination)
      parsedPagination.analyticsContextKey =
        parsedPagination?.analyticsContextKey ?? 'ts-data-table'
      return parsedPagination
    } catch (e) {
      console.error('Invalid JSON:', e)
    }
    return null
  }

  private reload() {
    // Deprecated: Use updateHeader() and updateRows() instead
    // Kept for backward compatibility if needed
    if (this.isLoading) {
      this.updateHeader()
      this.updatePaginationUi()
      this.renderSkeletonRows()
      return
    }

    this.updateHeader()
    this.updateRows()
  }

  private updateHeader() {
    // Only update the header structure
    // Called when columns, select options, or other header-level config changes
    const tblColumnsRoot = this.shadowRoot?.querySelector('thead tr')
    if (tblColumnsRoot) {
      tblColumnsRoot.innerHTML = '' // Clear existing headers

      // Add checkbox header if selection is enabled
      if (this.enableSelectColumn) {
        const visibleRowKeys = getVisibleRowKeys(this.dataRows, this.pageInfo)
        const allSelected = areAllVisibleRowsSelected(this.selectedRowKeys, visibleRowKeys)

        const checkboxHeaderTh = createSelectColumnHeader(
          this.enableSelectColumnHeader,
          (isChecked) => {
            this.handleSelectAllChange(isChecked)

            // Dispatch event
            const event = new CustomEvent('selectedColumnHeaderChanged', {
              detail: { selected: isChecked } as SelectedColumnHeaderChangedEvent,
              bubbles: true,
              composed: true,
            })
            this.dispatchEvent(event)
          },
          allSelected,
          document,
        )

        this.internalTryAddDependencies(document, [WebAwesomeComponent.Checkbox])
        tblColumnsRoot.appendChild(checkboxHeaderTh)
      }

      for (const column of this.dataColumns) {
        const th = document.createElement('th')
        th.textContent = column.text || ''
        th.part = 'header'
        if (column.sortable) {
          this.internalTryAddDependencies(document, [
            WebAwesomeComponent.Icon,
            WebAwesomeComponent.Button,
          ])

          const btnTemplate = sortableHeaderTemplate.content.cloneNode(true) as HTMLElement
          const waButton = btnTemplate.querySelector('wa-button')
          if (waButton) {
            // Disable button if loading
            if (this.isLoading) {
              waButton.setAttribute('disabled', '')
            } else {
              waButton.removeAttribute('disabled')
            }

            if (column.key == this.sortColumn) {
              btnTemplate
                .querySelector('wa-icon')
                ?.setAttribute('src', this.sortDirection === 'asc' ? svgSortUp : svgSortDown)
            }

            // Handle both click and keyboard events (Enter, Space)
            const handleSort = () => {
              if (this.pageInfo) {
                this.pageInfo.currentPage = 1
              }

              // Toggle sort direction if same column, otherwise default to 'asc'
              if (this.sortColumn === column.key) {
                this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
                this.sortColumnDataType = column.dataType || 'string'
              } else {
                this.sortColumn = column.key || null
                this.sortDirection = 'asc'
                this.sortColumnDataType = column.dataType || 'string'
              }

              // Fire event for consumers
              const sortEvent = new CustomEvent('sortChanged', {
                detail: {
                  key: column.key || null,
                  text: column.text,
                  direction: this.sortDirection,
                } as SortChangedEvent,
                bubbles: true,
                composed: true,
                cancelable: true,
              })

              this.dispatchEvent(sortEvent)

              // If consumer overrides → stop default sorting
              if (sortEvent.defaultPrevented) {
                return
              }

              // Fire analytics event for table sorting
              dispatchAnalyticEvent('ascend_table_sorted', 'click', this.componentName, {
                column: `${column.text || 'unknown'}`,
                direction: `${this.sortDirection}`,
              })

              // Only update rows, preserve header (and focus)
              this.updateRows()
            }

            // Only attach event listeners if NOT loading
            if (!this.isLoading) {
              waButton.addEventListener('click', handleSort)

              // Add keyboard support for Enter and Space keys
              waButton.addEventListener('keydown', ((e: KeyboardEvent) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault() // Prevent default button behavior
                  handleSort()
                }
              }) as EventListener)
            }
          }
          th.appendChild(btnTemplate)
        }
        tblColumnsRoot.appendChild(th)
      }
    }
  }

  private updateRows() {
    // Only update the table body and pagination
    // Does NOT touch the header, preserving focus on sort buttons
    this.updatePaginationUi()

    if (this.sortColumn) {
      this.dataRows.sort((a, b) => {
        const aValue = a.data?.find((cell) => cell.colKey === this.sortColumn)?.value || ''
        const bValue = b.data?.find((cell) => cell.colKey === this.sortColumn)?.value || ''

        // handle date sorting by looking at column data type
        if (this.sortColumnDataType === 'date') {
          const aDate = new Date(aValue as string)
          const bDate = new Date(bValue as string)
          if (isNaN(aDate.getTime()) || isNaN(bDate.getTime())) {
            return 0
          }
          if (aDate < bDate) return this.sortDirection === 'asc' ? -1 : 1
          if (aDate > bDate) return this.sortDirection === 'asc' ? 1 : -1
          return 0
        }

        if (aValue < bValue) return this.sortDirection === 'asc' ? -1 : 1
        if (aValue > bValue) return this.sortDirection === 'asc' ? 1 : -1
        return 0
      })
    }

    // Update header with correct sort icon when rows are updated
    // (but don't recreate the whole header structure)
    this.updateSortIconsInHeader()

    // set rows and cells
    const tblBody = this.shadowRoot?.querySelector('tbody')
    if (!tblBody) return

    tblBody.innerHTML = '' // Clear existing rows

    let dataToDisplay = this.dataRows
    if (this.pageInfo != null) {
      const start = (this.pageInfo.currentPage - 1) * this.pageInfo.pageSize
      const end = start + this.pageInfo.pageSize
      dataToDisplay = dataToDisplay.slice(start, end)
    }

    const columnCountWithSelection = this.enableSelectColumn
      ? this.dataColumns.length + 1
      : this.dataColumns.length

    if (this.noResultsShow || (dataToDisplay.length === 0 && this.noResultsAutoShow)) {
      const noContentRow = createNoContentRow(columnCountWithSelection)
      tblBody.appendChild(noContentRow)
      return
    }

    for (const row of dataToDisplay) {
      const tr = document.createElement('tr')
      tr.part = 'row'
      if (row.key && this.selectedRowKeys.has(row.key)) {
        tr.classList.add('row-selected')
      }

      // Add checkbox cell if selection is enabled
      if (this.enableSelectColumn) {
        this.internalTryAddDependencies(document, [WebAwesomeComponent.Checkbox])

        const checkboxTd = createSelectColumnCell(
          row.key || '',
          row.key ? this.selectedRowKeys.has(row.key) : false,
          (rowKey, isChecked) => {
            this.handleRowCheckboxChange(rowKey, isChecked)

            // Dispatch event
            const event = new CustomEvent('selectedItemChanged', {
              detail: { rowKey, selected: isChecked } as SelectedItemChangedEvent,
              bubbles: true,
              composed: true,
            })
            this.dispatchEvent(event)
          },
          document,
        )
        tr.appendChild(checkboxTd)
      }

      for (const cell of row.data || []) {
        if (cell.type === 'slot') {
          const slotElement = createSlotCell(cell as TableRowCellSlot)
          tr.appendChild(slotElement)
        } else if (cell.type === 'button-link') {
          this.internalTryAddDependencies(document, [WebAwesomeComponent.Button])

          const buttonLinkCell = createButtonLinkCell(
            this.getAttribute('spacing') === 'condensed',
            cell as TableRowCellButtonLink,
            this.componentName,
          )
          tr.appendChild(buttonLinkCell)
        } else if (cell.type === 'button-click') {
          this.internalTryAddDependencies(document, [WebAwesomeComponent.Button])
          const cellButtonClick = createButtonClickCell(
            cell as TableRowCellButtonClick,
            this.componentName,
            this,
            this._callback,
          )
          tr.appendChild(cellButtonClick)
        } else if (cell.type === 'dropdown') {
          this.internalTryAddDependencies(document, [
            WebAwesomeComponent.Button,
            WebAwesomeComponent.Dropdown,
            WebAwesomeComponent.DropdownItem,
          ])

          const cellDropdown = createDropdownCell(
            cell as TableRowCellDropdown,
            this.componentName,
            this,
            this._callback,
          )
          tr.appendChild(cellDropdown)
        } else if (cell.subtext && cell.subtext.length > 0) {
          const cellSubtext = createSubtextCell(cell)
          tr.appendChild(cellSubtext)
        } else {
          const td = document.createElement('td')
          td.part = 'cell'
          td.textContent = cell.text || ''
          tr.appendChild(td)
        }
      }
      tblBody.appendChild(tr)
    }
  }

  private updateSortIconsInHeader() {
    // Update only the sort icons in the existing header
    // This preserves all DOM elements and focus
    const headerCells = this.shadowRoot?.querySelectorAll('th')
    if (!headerCells) return

    // Skip first th if it's a checkbox column
    const startIndex = this.enableSelectColumn ? 1 : 0
    const displayColumns = this.enableSelectColumn ? this.dataColumns : this.dataColumns

    for (let i = 0; i < displayColumns.length; i++) {
      const column = displayColumns[i]
      const th = headerCells[startIndex + i]
      if (!th || !column.sortable) continue

      const icon = th.querySelector('wa-icon')
      if (!icon) continue

      if (column.key === this.sortColumn) {
        icon.setAttribute('src', this.sortDirection === 'asc' ? svgSortUp : svgSortDown)
      } else {
        icon.setAttribute('src', svgSortDown) // Reset to default sort icon
      }
    }
  }

  private updatePaginationUi() {
    const paginationWrapper = this.shadowRoot?.querySelector('.pagination-wrapper')
    if (!this.shadowRoot || !paginationWrapper) {
      return
    }

    // Hide pagination when loading
    if (this.isLoading) {
      paginationWrapper.classList.add('hide')
      return
    }

    if (this.pageInfo == null || (this.pageInfo.autoHide && this.pageInfo.totalPages <= 1)) {
      paginationWrapper.classList.add('hide')
      return
    }

    paginationWrapper.classList.remove('hide')

    const pc =
      this.shadowRoot.getElementById('pagination') ??
      this.createPaginationControl(paginationWrapper)

    pc.setAttribute('data', JSON.stringify(this.pageInfo))
  }

  private createPaginationControl(container: Element): HTMLElement {
    const pc = document.createElement('ts-pagination')
    pc.setAttribute('id', 'pagination')
    container.appendChild(pc)

    // Listen for page selection events
    pc.addEventListener('page-selected', (e: Event) => {
      const customEvent = e as CustomEvent<{ page: number }>
      if (this.pageInfo) {
        this.pageInfo.currentPage = customEvent.detail.page || 1
        this.updateRows()
      }
    })

    return pc
  }

  private internalTryAddDependencies(document: Document, components: WebAwesomeComponent[]) {
    components.forEach((component) => {
      if (!this.loadedDependencies.has(component)) {
        tryLoadDependencies(document, [component])
        this.loadedDependencies.add(component)
      }
    })
  }

  private handleRowCheckboxChange(rowKey: string, isChecked: boolean): void {
    if (isChecked) {
      this.selectedRowKeys.add(rowKey)
    } else {
      this.selectedRowKeys.delete(rowKey)
    }

    // Update all row checkbox states and row background colors
    const visibleRowKeys = getVisibleRowKeys(this.dataRows, this.pageInfo)
    updateAllRowCheckboxesInDOM(this.shadowRoot ?? null, visibleRowKeys, this.selectedRowKeys)
  }

  private handleSelectAllChange(isChecked: boolean): void {
    const visibleRowKeys = getVisibleRowKeys(this.dataRows, this.pageInfo)

    if (isChecked) {
      for (const key of visibleRowKeys) {
        this.selectedRowKeys.add(key)
      }
    } else {
      for (const key of visibleRowKeys) {
        this.selectedRowKeys.delete(key)
      }
    }

    this.updateCheckboxStates()
  }

  private updateSelectAllCheckboxState(): void {
    const visibleRowKeys = getVisibleRowKeys(this.dataRows, this.pageInfo)
    const allSelected = areAllVisibleRowsSelected(this.selectedRowKeys, visibleRowKeys)
    updateSelectAllCheckboxInDOM(this.shadowRoot, allSelected)
  }

  private updateCheckboxStates(): void {
    const visibleRowKeys = getVisibleRowKeys(this.dataRows, this.pageInfo)
    updateAllRowCheckboxesInDOM(this.shadowRoot, visibleRowKeys, this.selectedRowKeys)
  }

  private renderSkeletonRows() {
    const tbody = this.shadowRoot?.querySelector('tbody')
    if (!tbody) {
      return
    }

    tbody.replaceChildren(createSkeletonRows(this.dataColumns, this.skeletonRowCount))
  }
}

if (!customElements.get('ts-data-table')) {
  customElements.define('ts-data-table', DataTable)
}
export { DataTable }
